import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import NativeSelect from "@mui/material/NativeSelect";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import {
  MAX_RESERVATION_OPERATIONS_OFFSET,
  searchPlatformReservationOperations,
  type ReservationOperationSort,
  type ReservationOperationStatus,
} from "@bw/shared/api";

import { PlatformPage } from "@/components/PlatformPage";

import {
  getReservationOrganization,
  listReservationFacilities,
  listReservationFacilitiesByIds,
  listReservationOrganizations,
} from "../api";
import { ReservationOperationsTable } from "../components/ReservationOperationsTable";

const PAGE_SIZE = 50;
const MAX_PAGE = Math.floor(MAX_RESERVATION_OPERATIONS_OFFSET / PAGE_SIZE) + 1;
const MAX_REACHABLE_RESULTS = MAX_RESERVATION_OPERATIONS_OFFSET + PAGE_SIZE;
const MAX_FACILITY_FILTERS = 100;
const OPTION_SEARCH_DEBOUNCE_MS = 250;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STATUSES: Array<{ value: ReservationOperationStatus; label: string }> = [
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "no_show", label: "No-Show" },
];
const VALID_STATUSES = new Set(STATUSES.map((item) => item.value));
const VALID_SORTS = new Set<ReservationOperationSort>([
  "start_time",
  "created_at",
  "status",
  "facility",
  "court",
  "player",
]);

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function validDate(value: string | null, fallback: string): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
    ? fallback
    : value;
}

function validUuid(value: string | null): value is string {
  return Boolean(value && UUID_PATTERN.test(value));
}

function addDays(date: Date, amount: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
}

function startOfCurrentWeek(now: Date): Date {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysSinceMonday = (start.getDay() + 6) % 7;
  return addDays(start, -daysSinceMonday);
}

function paginationLabel(type: string, page: number | null): string {
  if (type === "next") return "Next";
  if (type === "previous") return "Previous";
  if (type === "first") return "First";
  if (type === "last") return "Last";
  return page == null ? "More pages" : `Page ${page}`;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);

  return debounced;
}

export function ReservationOperationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsKey = searchParams.toString();
  const now = new Date();
  const today = toIsoDate(now);
  const fromDate = validDate(searchParams.get("from"), today);
  const requestedToDate = validDate(searchParams.get("to"), fromDate);
  const toDate = requestedToDate < fromDate ? fromDate : requestedToDate;
  const requestedOrganizationId = searchParams.get("organization");
  const organizationId = validUuid(requestedOrganizationId)
    ? requestedOrganizationId
    : "";
  const requestedFacilityIds = useMemo(
    () => new URLSearchParams(paramsKey).getAll("facility"),
    [paramsKey],
  );
  const selectedFacilityIds = useMemo(() => {
    const selected = new Set<string>();
    for (const facilityId of requestedFacilityIds) {
      if (validUuid(facilityId)) selected.add(facilityId);
      if (selected.size === MAX_FACILITY_FILTERS) break;
    }
    return [...selected];
  }, [requestedFacilityIds]);
  const organizationNeedsCanonicalization =
    requestedOrganizationId !== null && !validUuid(requestedOrganizationId);
  const facilitiesNeedCanonicalization =
    requestedFacilityIds.length !== selectedFacilityIds.length ||
    requestedFacilityIds.some(
      (facilityId, index) => facilityId !== selectedFacilityIds[index],
    );
  const filtersCanonical =
    !organizationNeedsCanonicalization && !facilitiesNeedCanonicalization;
  const selectedStatuses = useMemo(
    () =>
      new URLSearchParams(paramsKey)
        .getAll("status")
        .filter((status): status is ReservationOperationStatus =>
          VALID_STATUSES.has(status as ReservationOperationStatus),
        ),
    [paramsKey],
  );
  const committedSearch = searchParams.get("q")?.trim() ?? "";
  const requestedSort = searchParams.get(
    "sort",
  ) as ReservationOperationSort | null;
  const sortBy =
    requestedSort && VALID_SORTS.has(requestedSort)
      ? requestedSort
      : "start_time";
  const sortDirection =
    searchParams.get("direction") === "desc" ? "desc" : "asc";
  const requestedPageParam = searchParams.get("page");
  const requestedPage = Number(requestedPageParam ?? "1");
  const normalizedPage =
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const page = Math.min(normalizedPage, MAX_PAGE);
  const pageNeedsCanonicalization =
    requestedPageParam !== null && requestedPageParam !== String(page);
  const [searchDraft, setSearchDraft] = useState(committedSearch);
  const [organizationOptionSearch, setOrganizationOptionSearch] = useState("");
  const [facilityOptionSearch, setFacilityOptionSearch] = useState("");
  const [facilityFiltersOpen, setFacilityFiltersOpen] = useState(false);
  const debouncedOrganizationOptionSearch = useDebouncedValue(
    organizationOptionSearch.trim(),
    OPTION_SEARCH_DEBOUNCE_MS,
  );
  const debouncedFacilityOptionSearch = useDebouncedValue(
    facilityOptionSearch.trim(),
    OPTION_SEARCH_DEBOUNCE_MS,
  );

  useEffect(() => {
    setSearchDraft(committedSearch);
  }, [committedSearch]);

  useEffect(() => {
    const next = new URLSearchParams(paramsKey);
    if (filtersCanonical && !pageNeedsCanonicalization) return;

    if (organizationNeedsCanonicalization) next.delete("organization");
    if (facilitiesNeedCanonicalization) {
      next.delete("facility");
      selectedFacilityIds.forEach((facilityId) =>
        next.append("facility", facilityId),
      );
    }
    if (pageNeedsCanonicalization) {
      if (page === 1) next.delete("page");
      else next.set("page", String(page));
    }
    setSearchParams(next, { replace: true });
  }, [
    facilitiesNeedCanonicalization,
    filtersCanonical,
    organizationNeedsCanonicalization,
    page,
    pageNeedsCanonicalization,
    paramsKey,
    selectedFacilityIds,
    setSearchParams,
  ]);

  const updateParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setRepeated = useCallback(
    (name: string, values: string[]) => {
      updateParams((next) => {
        next.delete(name);
        values.forEach((value) => next.append(name, value));
        next.delete("page");
      });
    },
    [updateParams],
  );

  const organizationsQuery = useQuery({
    queryKey: [
      "platform",
      "reservation-operations",
      "organizations",
      debouncedOrganizationOptionSearch,
    ],
    queryFn: ({ signal }) =>
      listReservationOrganizations(debouncedOrganizationOptionSearch, signal),
  });
  const facilitiesQuery = useQuery({
    queryKey: [
      "platform",
      "reservation-operations",
      "facilities",
      organizationId,
      debouncedFacilityOptionSearch,
    ],
    queryFn: ({ signal }) =>
      listReservationFacilities(
        organizationId || undefined,
        debouncedFacilityOptionSearch,
        signal,
      ),
  });
  const selectedOrganizationQuery = useQuery({
    queryKey: [
      "platform",
      "reservation-operations",
      "organization",
      organizationId,
    ],
    queryFn: ({ signal }) => getReservationOrganization(organizationId, signal),
    enabled: Boolean(organizationId),
  });
  const selectedFacilitiesQuery = useQuery({
    queryKey: [
      "platform",
      "reservation-operations",
      "selected-facilities",
      selectedFacilityIds,
    ],
    queryFn: ({ signal }) =>
      listReservationFacilitiesByIds(selectedFacilityIds, signal),
    enabled: selectedFacilityIds.length > 0,
  });
  const reservationsQuery = useQuery({
    queryKey: ["platform", "reservation-operations", paramsKey],
    queryFn: () =>
      searchPlatformReservationOperations({
        fromDate,
        toDate,
        organizationIds: organizationId ? [organizationId] : undefined,
        facilityIds:
          selectedFacilityIds.length > 0 ? selectedFacilityIds : undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        query: committedSearch || undefined,
        sortBy,
        sortDirection,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    enabled: filtersCanonical && !pageNeedsCanonicalization,
    // Every platform lookup writes an audit entry. Only an explicit URL/filter
    // change may issue one; focus, reconnect, and retry automation must not
    // manufacture privileged searches the operator did not request.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const organizations = useMemo(() => {
    const byId = new Map(
      (organizationsQuery.data?.items ?? []).map((organization) => [
        organization.org_id,
        organization,
      ]),
    );
    const selected = selectedOrganizationQuery.data;
    if (selected) byId.set(selected.org_id, selected);
    return [...byId.values()];
  }, [organizationsQuery.data?.items, selectedOrganizationQuery.data]);
  const facilities = useMemo(() => {
    const byId = new Map(
      (facilitiesQuery.data?.items ?? []).map((facility) => [
        facility.facility_id,
        facility,
      ]),
    );
    for (const selected of selectedFacilitiesQuery.data?.items ?? []) {
      byId.set(selected.facility_id, selected);
    }
    return [...byId.values()];
  }, [facilitiesQuery.data?.items, selectedFacilitiesQuery.data?.items]);
  const filteredFacilities = useMemo(() => {
    const query = facilityOptionSearch.trim().toLocaleLowerCase();
    if (!query) return facilities;
    const selectedIds = new Set(selectedFacilityIds);
    return facilities.filter(
      (facility) =>
        selectedIds.has(facility.facility_id) ||
        `${facility.name} ${facility.organization_name}`
          .toLocaleLowerCase()
          .includes(query),
    );
  }, [facilities, facilityOptionSearch, selectedFacilityIds]);
  const facilityOptionsPending =
    facilityOptionSearch.trim() !== debouncedFacilityOptionSearch ||
    facilitiesQuery.isFetching ||
    selectedFacilitiesQuery.isFetching;
  const reservations = reservationsQuery.data?.items ?? [];
  const total = reservationsQuery.data?.total ?? 0;
  const reservationsPending =
    !filtersCanonical ||
    pageNeedsCanonicalization ||
    reservationsQuery.isLoading;
  const hasAuthoritativeResults =
    reservationsQuery.isSuccess && !reservationsPending;
  const selectedFacilityLabel =
    selectedFacilityIds.length === 0
      ? "All facilities"
      : selectedFacilityIds.length === 1
        ? (() => {
            const selectedFacility = facilities.find(
              (facility) => facility.facility_id === selectedFacilityIds[0],
            );
            return selectedFacility
              ? `${selectedFacility.name} - ${selectedFacility.organization_name}`
              : "1 facility";
          })()
        : `${selectedFacilityIds.length} facilities`;

  const applyPreset = (
    preset: "today" | "yesterday" | "this_week" | "last_week",
  ) => {
    let start = now;
    let end = now;
    if (preset === "yesterday") {
      start = addDays(now, -1);
      end = start;
    } else if (preset === "this_week") {
      start = startOfCurrentWeek(now);
      end = addDays(start, 6);
    } else if (preset === "last_week") {
      start = addDays(startOfCurrentWeek(now), -7);
      end = addDays(start, 6);
    }
    updateParams((next) => {
      next.set("from", toIsoDate(start));
      next.set("to", toIsoDate(end));
      next.delete("page");
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const reachableTotalPages = Math.min(totalPages, MAX_PAGE);

  useEffect(() => {
    if (reservationsQuery.isSuccess && page > reachableTotalPages) {
      updateParams((next) => next.set("page", String(reachableTotalPages)));
    }
  }, [page, reachableTotalPages, reservationsQuery.isSuccess, updateParams]);

  return (
    <PlatformPage title="Reservation Operations">
      <Stack spacing={2.5}>
        <Paper
          component="section"
          aria-label="Reservation filters"
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1fr) minmax(190px, 0.45fr) minmax(220px, 0.55fr)",
                },
                gap: 2,
              }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  aria-label="Date presets"
                  sx={{ flexWrap: "wrap" }}
                >
                  {[
                    ["today", "Today"],
                    ["yesterday", "Yesterday"],
                    ["this_week", "This week"],
                    ["last_week", "Last week"],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        applyPreset(value as Parameters<typeof applyPreset>[0])
                      }
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    type="date"
                    size="small"
                    label="From date"
                    value={fromDate}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={(event) =>
                      updateParams((next) => {
                        next.set("from", event.target.value);
                        if (event.target.value > toDate) {
                          next.set("to", event.target.value);
                        }
                        next.delete("page");
                      })
                    }
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="To date"
                    value={toDate}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={(event) =>
                      updateParams((next) => {
                        next.set("to", event.target.value);
                        if (event.target.value < fromDate) {
                          next.set("from", event.target.value);
                        }
                        next.delete("page");
                      })
                    }
                  />
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <TextField
                  type="search"
                  size="small"
                  label="Find organization"
                  value={organizationOptionSearch}
                  slotProps={{
                    htmlInput: { "aria-label": "Search organization options" },
                  }}
                  onChange={(event) =>
                    setOrganizationOptionSearch(event.target.value)
                  }
                />
                <FormControl variant="standard" size="small">
                  <InputLabel htmlFor="reservation-organization">
                    Organization
                  </InputLabel>
                  <NativeSelect
                    value={organizationId}
                    inputProps={{ id: "reservation-organization" }}
                    onChange={(event) =>
                      updateParams((next) => {
                        if (event.target.value) {
                          next.set("organization", event.target.value);
                        } else {
                          next.delete("organization");
                        }
                        setFacilityOptionSearch("");
                        next.delete("facility");
                        next.delete("page");
                      })
                    }
                  >
                    <option value="">All organizations</option>
                    {organizations.map((organization) => (
                      <option
                        key={organization.org_id}
                        value={organization.org_id}
                      >
                        {organization.business_name ||
                          organization.legal_name ||
                          "Unnamed organization"}
                      </option>
                    ))}
                  </NativeSelect>
                </FormControl>
              </Stack>

              <Box
                component="details"
                open={facilityFiltersOpen}
                onToggle={(event) =>
                  setFacilityFiltersOpen(event.currentTarget.open)
                }
                sx={{ alignSelf: "end", position: "relative" }}
              >
                <Box
                  component="summary"
                  aria-expanded={facilityFiltersOpen}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" ||
                      event.key === " " ||
                      event.key === "Space" ||
                      event.key === "Spacebar"
                    ) {
                      event.preventDefault();
                      setFacilityFiltersOpen((open) => !open);
                    }
                  }}
                  sx={{
                    alignItems: "center",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    minHeight: 40,
                    px: 1.5,
                    typography: "body2",
                  }}
                >
                  <Box component="span">{selectedFacilityLabel}</Box>
                  <Box component="span" aria-hidden="true">
                    {facilityFiltersOpen ? "−" : "+"}
                  </Box>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 320,
                    mt: 0.5,
                    overflowY: "auto",
                    p: 1,
                    position: "absolute",
                    right: 0,
                    width: "min(32rem, calc(100vw - 2rem))",
                    zIndex: 20,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "background.paper",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <TextField
                      type="search"
                      size="small"
                      fullWidth
                      label="Search facilities"
                      value={facilityOptionSearch}
                      slotProps={{
                        htmlInput: { "aria-label": "Search facility options" },
                      }}
                      onChange={(event) =>
                        setFacilityOptionSearch(event.target.value)
                      }
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedFacilityIds.length === 0}
                        onChange={() => setRepeated("facility", [])}
                      />
                    }
                    label="All facilities"
                    sx={{ m: 0, py: 0.5 }}
                  />
                  {filteredFacilities.map((facility) => (
                    <FormControlLabel
                      key={facility.facility_id}
                      control={
                        <Checkbox
                          size="small"
                          slotProps={{
                            input: {
                              "aria-label": `${facility.name} - ${facility.organization_name}`,
                            },
                          }}
                          checked={selectedFacilityIds.includes(
                            facility.facility_id,
                          )}
                          disabled={
                            selectedFacilityIds.length >=
                              MAX_FACILITY_FILTERS &&
                            !selectedFacilityIds.includes(facility.facility_id)
                          }
                          onChange={() => {
                            const next = selectedFacilityIds.includes(
                              facility.facility_id,
                            )
                              ? selectedFacilityIds.filter(
                                  (id) => id !== facility.facility_id,
                                )
                              : [...selectedFacilityIds, facility.facility_id];
                            setRepeated("facility", next);
                          }}
                        />
                      }
                      label={
                        <Box sx={{ minWidth: 0, py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {facility.name}
                          </Typography>
                          <Typography color="text.secondary" variant="caption">
                            {facility.organization_name}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        alignItems: "flex-start",
                        borderTop: 1,
                        borderColor: "divider",
                        display: "flex",
                        m: 0,
                        width: "100%",
                      }}
                    />
                  ))}
                  {facilityOptionsPending && (
                    <Typography
                      role="status"
                      color="text.secondary"
                      variant="body2"
                      sx={{ px: 1, py: 2 }}
                    >
                      Loading facilities...
                    </Typography>
                  )}
                  {!facilityOptionsPending &&
                    filteredFacilities.length === 0 && (
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{ px: 1, py: 2 }}
                      >
                        No facilities match this search.
                      </Typography>
                    )}
                  {(facilitiesQuery.data?.total ?? 0) > facilities.length && (
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        borderTop: 1,
                        borderColor: "divider",
                        display: "block",
                        p: 1,
                      }}
                    >
                      {facilities.length.toLocaleString()} of{" "}
                      {facilitiesQuery.data?.total.toLocaleString()} facilities
                    </Typography>
                  )}
                </Paper>
              </Box>
            </Box>

            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              sx={{
                alignItems: { lg: "flex-end" },
                justifyContent: "space-between",
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                aria-label="Reservation status"
                sx={{ flexWrap: "wrap" }}
              >
                <Button
                  type="button"
                  size="small"
                  aria-pressed={selectedStatuses.length === 0}
                  variant={
                    selectedStatuses.length === 0 ? "contained" : "outlined"
                  }
                  onClick={() => setRepeated("status", [])}
                >
                  All
                </Button>
                {STATUSES.map((status) => {
                  const active = selectedStatuses.includes(status.value);
                  return (
                    <Button
                      key={status.value}
                      type="button"
                      size="small"
                      aria-pressed={active}
                      variant={active ? "contained" : "outlined"}
                      onClick={() =>
                        setRepeated(
                          "status",
                          active
                            ? selectedStatuses.filter(
                                (value) => value !== status.value,
                              )
                            : [...selectedStatuses, status.value],
                        )
                      }
                    >
                      {status.label}
                    </Button>
                  );
                })}
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: { xs: "100%", lg: "min(100%, 42rem)" } }}
              >
                <Box
                  component="form"
                  sx={{ display: "flex", flex: 1, gap: 1, minWidth: 0 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    updateParams((next) => {
                      const value = searchDraft.trim();
                      if (value) next.set("q", value);
                      else next.delete("q");
                      next.delete("page");
                    });
                  }}
                >
                  <TextField
                    type="search"
                    size="small"
                    fullWidth
                    label="Search reservations"
                    placeholder="Player, email, booking ID, or court"
                    value={searchDraft}
                    onChange={(event) => setSearchDraft(event.target.value)}
                  />
                  <Button type="submit" size="small" variant="contained">
                    Search
                  </Button>
                </Box>
                <FormControl
                  variant="standard"
                  size="small"
                  sx={{ minWidth: 190 }}
                >
                  <InputLabel htmlFor="reservation-sort">Sort</InputLabel>
                  <NativeSelect
                    value={`${sortBy}:${sortDirection}`}
                    inputProps={{ id: "reservation-sort" }}
                    onChange={(event) => {
                      const [nextSort, nextDirection] =
                        event.target.value.split(":");
                      updateParams((next) => {
                        next.set("sort", nextSort);
                        next.set("direction", nextDirection);
                        next.delete("page");
                      });
                    }}
                  >
                    <option value="start_time:asc">Start time, earliest</option>
                    <option value="start_time:desc">Start time, latest</option>
                    <option value="created_at:desc">Recently created</option>
                    <option value="facility:asc">Facility name</option>
                    <option value="player:asc">Player name</option>
                  </NativeSelect>
                </FormControl>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        {(organizationsQuery.isError ||
          facilitiesQuery.isError ||
          selectedOrganizationQuery.isError ||
          selectedFacilitiesQuery.isError) && (
          <Alert severity="warning" role="alert">
            Some organization or facility filter options could not be loaded.
          </Alert>
        )}

        {reservationsQuery.isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => reservationsQuery.refetch()}
              >
                Try again
              </Button>
            }
          >
            Reservation operations could not be loaded.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography component="h2" variant="h6">
                Reservations
              </Typography>
              {hasAuthoritativeResults && (
                <Typography color="text.secondary" variant="body2">
                  {total.toLocaleString()} result{total === 1 ? "" : "s"}
                </Typography>
              )}
            </Stack>

            <ReservationOperationsTable
              items={reservations}
              total={total}
              isLoading={reservationsPending}
            />

            {hasAuthoritativeResults && totalPages > reachableTotalPages && (
              <Alert severity="warning">
                Only the first {MAX_REACHABLE_RESULTS.toLocaleString()} matches
                are available. Narrow filters to reach later results.
              </Alert>
            )}

            {hasAuthoritativeResults && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography color="text.secondary" variant="body2">
                  Page {Math.min(page, reachableTotalPages)} of {totalPages}
                </Typography>
                <Pagination
                  page={Math.min(page, reachableTotalPages)}
                  count={reachableTotalPages}
                  size="small"
                  getItemAriaLabel={(type, itemPage) =>
                    paginationLabel(type, itemPage)
                  }
                  onChange={(_event, nextPage) =>
                    updateParams((next) => next.set("page", String(nextPage)))
                  }
                />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </PlatformPage>
  );
}
